import {
  HIGH_LOW_LABEL_GAP_PX,
  HIGH_LOW_LABEL_HEIGHT_PX,
  HIGH_LOW_LABEL_PADDING_X_PX,
  HIGH_LOW_LABEL_RADIUS_PX,
  HIGH_LOW_TIMER_SPACING_PX,
} from "../constants";
import { formatPrice } from "../axes/priceFormat";
import { roundedRectPath } from "../text/roundedRect";

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const intersects = (first, second) =>
  first.x < second.x + second.width &&
  second.x < first.x + first.width &&
  first.y < second.y + second.height &&
  second.y < first.y + first.height;

// Centred on the bar, kept inside the pane; moved left of the bar timer when it would cover it
const placeLabel = ({ x, top, width, pane, timerRect }) => {
  const maxX = pane.width - width;
  const rect = {
    x: clamp(x - width / 2, 0, maxX),
    y: clamp(top, 0, pane.height - HIGH_LOW_LABEL_HEIGHT_PX),
    width,
    height: HIGH_LOW_LABEL_HEIGHT_PX,
  };

  if (timerRect && intersects(rect, timerRect)) {
    rect.x = clamp(timerRect.x - HIGH_LOW_TIMER_SPACING_PX - width, 0, maxX);
  }

  return rect;
};

/**
 * High / low labels of the visible bars (po-chart-visual-spec.md §12.6):
 * the price at the full pair precision (E9) in a rounded semi-transparent
 * box, centred above the highest wick and below the lowest one. A label is
 * shown only while its bar and its price are in the pane (a manual scale or
 * zoom can push the extreme out); it is kept inside the pane, so it never
 * reaches the price axis, and does not cover the bar timer.
 *
 * @param {object} textLayer
 * @param {{ high: { index: number, price: number }, low: { index: number, price: number } }} extremes -
 *   mainSeries.extremes
 * @param {object} params
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {{ width: number, height: number }} params.pane
 * @param {number} params.decimals - price precision of the pair
 * @param {{ x: number, y: number, width: number, height: number } | null} params.timerRect
 * @param {string} params.font
 * @param {{ background: string, color: string }} params.style - CSS colours
 */
export const drawHighLowLabels = (
  textLayer,
  extremes,
  { timeScale, priceScale, pane, decimals, timerRect, font, style },
) => {
  const { context } = textLayer;
  const labels = [
    { extreme: extremes.high, offset: -HIGH_LOW_LABEL_GAP_PX - HIGH_LOW_LABEL_HEIGHT_PX },
    { extreme: extremes.low, offset: HIGH_LOW_LABEL_GAP_PX },
  ];

  labels.forEach(({ extreme, offset }) => {
    const x = timeScale.indexToX(extreme.index);
    const y = priceScale.priceToY(extreme.price);

    if (x < 0 || x > pane.width || y < 0 || y > pane.height) return;

    const top = y + offset;

    const text = formatPrice(extreme.price, decimals);
    const width = textLayer.measure(text, font) + 2 * HIGH_LOW_LABEL_PADDING_X_PX;
    const rect = placeLabel({ x, top, width, pane, timerRect });

    roundedRectPath(context, rect, HIGH_LOW_LABEL_RADIUS_PX);
    context.fillStyle = style.background;
    context.fill();
    textLayer.drawText(text, rect.x + width / 2, rect.y + HIGH_LOW_LABEL_HEIGHT_PX / 2, {
      font,
      color: style.color,
      align: "center",
    });
  });
};
